import { DownloadIcon, Eye, MessageCircleIcon } from "lucide-react";

import { Button } from "@/components/button";
import { FILE_ICONS } from "@/features/my-files/assets/file-icons";
import { useUser } from "@/hooks/useUser";

import type { ChatStory } from "../../types";

import { getAttachmentDisplayName } from "../../utils/attachment-display-name";

const CUSTOMER_IDS = new Set([2, 3, 4]);

// Supported file types for preview
const PREVIEW_SUPPORTED_FORMATS = [
  // Images
  ".png",
  ".bmp",
  ".webp",
  ".jpg",
  ".jpeg",
  ".svg",
  // Videos
  ".avi",
  ".mov",
  ".mkv",
  ".mp4",
  ".m4v",
  ".gif",
  // PDFs
  ".pdf",
];

export function RandomFile({
  data,
  handleFeedback,
  handleOpenPreview,
}: {
  data: ChatStory;
  handleFeedback?: (groupId: string) => void;
  handleOpenPreview: (id: number) => void;
}) {
  const fileExtension = data.attachment?.name?.split(".").pop();
  const displayName = getAttachmentDisplayName(data.attachment, data.comment);
  const IconComponent =
    fileExtension && fileExtension in FILE_ICONS
      ? FILE_ICONS[fileExtension as keyof typeof FILE_ICONS]
      : FILE_ICONS.fallback;

  const isPreviewSupported = () => {
    if (!data.attachment?.name) return false;
    const fileName = data.attachment.name.toLowerCase();

    return PREVIEW_SUPPORTED_FORMATS.some((format) =>
      fileName.endsWith(format),
    );
  };

  const { data: user } = useUser();

  const isCustomer = CUSTOMER_IDS.has(user?.user?.roleId || 0);
  const isAdmin = user?.user?.roleId === 1;

  const handlePreviewClick = () => {
    handleOpenPreview(data?.attachmentId || 0);
  };

  const downloadFile = async () => {
    if (!data.attachment?.url) return;

    try {
      const response = await fetch(data.attachment.url);
      const blob = await response.blob();

      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = downloadUrl;
      link.download = data.attachment.name || "download";
      document.body.append(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      window.open(data.attachment.url, "_blank");
    }
  };

  return (
    <div className="flex flex-col items-center justify-between gap-2">
      <div className="flex items-center gap-2 w-full">
        <div className="relative group">
          <IconComponent />
          {isPreviewSupported() && (
            <div
              className="absolute inset-0 flex items-center justify-center transition-all duration-300 bg-transparent group-hover:bg-black/50 rounded cursor-pointer"
              onClick={handlePreviewClick}
              title="Preview file"
            >
              <Eye
                className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                size={20}
              />
            </div>
          )}
        </div>
        <div className="flex-1">
          <p
            className="text-sm text-pretty font-bold text-base/6 sm:text-sm/6"
            title={displayName}
          >
            {displayName}
          </p>
          {isPreviewSupported() && (
            <button
              className="text-xs text-blue-500 hover:text-blue-700 underline mt-1 cursor-pointer"
              onClick={handlePreviewClick}
              type="button"
            >
              Preview
            </button>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          className="border border-button-secondary-border px-6"
          onPress={downloadFile}
          variant="plain"
        >
          <DownloadIcon />
          Download
        </Button>
        {handleFeedback &&
          fileExtension === "pdf" &&
          (isCustomer || isAdmin) && (
            <Button
              className="border border-button-secondary-border px-6"
              onPress={() => {
                console.log("handleFeedback", data.attachmentId);
                handleFeedback(data.attachmentId?.toString() || "");
              }}
              variant="primary"
            >
              <MessageCircleIcon />
              Feedback
            </Button>
          )}
      </div>
    </div>
  );
}
